import { useEffect, useState } from "react";

declare global {
  interface Window {
    google?: any;
    __sedGoogleMapsLoading?: Promise<void>;
  }
}

const SCRIPT_ID = "sed-google-maps-script";

const placesReady = () =>
  Boolean(window.google?.maps?.places?.AutocompleteSuggestion);

export const useGoogleMaps = (apiKey: string | null | undefined): boolean => {
  const [isLoaded, setLoaded] = useState<boolean>(placesReady());

  useEffect(() => {
    if (!apiKey) return;
    if (placesReady()) {
      setLoaded(true);
      return;
    }

    if (!window.__sedGoogleMapsLoading) {
      window.__sedGoogleMapsLoading = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID) as
          | HTMLScriptElement
          | null;
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () =>
            reject(new Error("Google Maps failed to load")),
          );
          return;
        }
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
          apiKey,
        )}&libraries=places&v=weekly&language=fr&region=FR&loading=async`;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Google Maps failed to load"));
        document.head.appendChild(script);
      });
    }

    let cancelled = false;
    window.__sedGoogleMapsLoading
      .then(() => {
        // `script.onload` fires before async-loaded places.js finishes,
        // so poll briefly for AutocompleteSuggestion to be exposed.
        const start = Date.now();
        const tick = () => {
          if (cancelled) return;
          if (placesReady()) {
            setLoaded(true);
            return;
          }
          if (Date.now() - start > 10_000) {
            // eslint-disable-next-line no-console
            console.warn(
              "[ServiceEnDirect] Places library did not load within 10s",
            );
            return;
          }
          setTimeout(tick, 50);
        };
        tick();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[ServiceEnDirect] Google Maps load failed:", e);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return isLoaded;
};
